import React, { useEffect, useState } from 'react';
import { RefreshCw, Bell, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { superuserService } from '../../services/api';

interface NotificationItem {
  id: number;
  user_id: number;
  type: string;
  titre: string;
  message: string;
  is_read: boolean;
  created_at: string;
  user_name: string;
  user_role: string;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}

const NOTIFICATION_TYPES = [
  { value: '', label: 'Tous les types' },
  { value: 'new_booking', label: 'Nouvelle réservation' },
  { value: 'booking_confirmed', label: 'Réservation confirmée' },
  { value: 'booking_cancelled', label: 'Réservation annulée' },
  { value: 'new_message', label: 'Nouveau message' },
  { value: 'payment_received', label: 'Paiement reçu' },
  { value: 'account_validated', label: 'Compte validé' },
  { value: 'system', label: 'Système' },
];

const AdminNotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, per_page: 20, total: 0, pages: 0 });
  const [typeFilter, setTypeFilter] = useState('');

  const fetchNotifications = async (page: number = 1) => {
    setLoading(true);
    try {
      const params: any = { page, per_page: 20 };
      if (typeFilter) {
        params.type = typeFilter;
      }
      const response = await superuserService.getAllNotifications(params);
      if (response.data?.notifications) {
        setNotifications(response.data.notifications);
        setPagination(response.data.pagination);
      }
    } catch (error: any) {
      console.error('Erreur chargement notifications:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1);
  }, [typeFilter]);

  const getRoleBadge = (role: string) => {
    if (role === 'ROLE_GERANT') {
      return <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">Gérant</span>;
    }
    if (role === 'ROLE_LOCATAIRE') {
      return <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Locataire</span>;
    }
    if (role === 'ROLE_ADMIN') {
      return <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">Admin</span>;
    }
    return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">{role}</span>;
  };

  const getTypeBadge = (type: string) => {
    const typeConfig: { [key: string]: { bg: string; text: string; label: string } } = {
      'new_booking': { bg: 'bg-green-100', text: 'text-green-700', label: 'Nouvelle réservation' },
      'booking_confirmed': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Confirmée' },
      'booking_cancelled': { bg: 'bg-red-100', text: 'text-red-700', label: 'Annulée' },
      'new_message': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Message' },
      'payment_received': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paiement' },
      'account_validated': { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Compte validé' },
      'system': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Système' },
    };
    const config = typeConfig[type] || { bg: 'bg-gray-100', text: 'text-gray-700', label: type };
    return <span className={`px-2 py-0.5 text-xs ${config.bg} ${config.text} rounded-full`}>{config.label}</span>;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Notifications</h1>
          <p className="text-neutral-600">Toutes les notifications envoyées aux utilisateurs</p>
        </div>
        <button onClick={() => fetchNotifications(pagination.page)} className="btn btn-outline flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2"
          >
            {NOTIFICATION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500">
            {pagination.total} notification{pagination.total > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Liste des notifications */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="card p-12 text-center">
          <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-neutral-500">Aucune notification</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notif) => (
            <div key={notif.id} className={`card p-4 ${!notif.is_read ? 'border-l-4 border-l-primary-500' : ''}`}>
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  notif.is_read ? 'bg-gray-100' : 'bg-primary-100'
                }`}>
                  <Bell className={`w-5 h-5 ${notif.is_read ? 'text-gray-500' : 'text-primary-500'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium">{notif.titre}</span>
                    {getTypeBadge(notif.type)}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{notif.message}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      Destinataire: <strong className="text-gray-600">{notif.user_name}</strong>
                      {getRoleBadge(notif.user_role)}
                    </span>
                    <span>{formatDate(notif.created_at)}</span>
                    {!notif.is_read && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">Non lu</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => fetchNotifications(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="btn btn-outline flex items-center gap-2 disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Précédent
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} sur {pagination.pages}
          </span>
          <button
            onClick={() => fetchNotifications(pagination.page + 1)}
            disabled={pagination.page >= pagination.pages}
            className="btn btn-outline flex items-center gap-2 disabled:opacity-50"
          >
            Suivant
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationsPage;
